import React from 'react';
import {
  HONEYBADGER_CONFIG,
  PHONE_NUMBER_MAX,
  PHONE_NUMBER_MIN,
  BID_STATUS,
  PROJECT_STATUS,
} from 'consts';

export function first(array) {
  return array && array.length ? array[0] : undefined;
}

export function scrollToTheTop(top = 0, behaviour = 'smooth') {
  window.scrollTo({ top: top, behavior: behaviour });
}

export function throttle(interval = 250) {
  let lastCalledAt = +new Date() - interval;
  let timeout = null;
  const fn = this;
  return function () {
    const args = [].slice.call(arguments);
    const calledAt = +new Date();
    const callIn = interval - Math.min(interval, calledAt - lastCalledAt);
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      fn.apply(this, args);
      lastCalledAt = calledAt;
    }, callIn);
  };
}

export async function catchErr(operation) {
  let err, val;
  try {
    val = await operation;
  } catch (e) {
    err = e;
  }
  return [err, val];
}

export function debounce(wait, immediate = false) {
  let timeout = null;
  const fn = this;
  return function (...args) {
    const callNow = immediate && !timeout;
    const next = () => {
      fn.apply(this, args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(next, wait);

    if (callNow) {
      next();
    }
  };
}

/** Removes elements from object with values matching the passed in `values`.
 *  - Usage: `shake.call(myObject, null, undefined)`
 *  - ES7 usage: `myObject::shake(null, undefined)`
 */
export function shake(...values) {
  const obj = Object.create(null, {});
  for (let i in this) {
    if (values.indexOf(this[i]) >= 0) {
      continue;
    }
    obj[i] = this[i];
  }
  return obj;
}

export function isEmptyObject() {
  for (let name in this) {
    return false;
  }
  return true;
}

export function objToQueryString(obj, keyPrefix = '') {
  return Object.keys(obj).reduce((final, current) => {
    let delimiter = '?';
    if (final.length) {
      delimiter = '&';
    }
    if (keyPrefix && !final) {
      delimiter = '';
    }
    const initialValue = obj[current];
    let key = keyPrefix
      ? `${keyPrefix}%5B${encodeURIComponent(current)}%5D`
      : encodeURIComponent(current);
    let value = null;
    if (
      typeof initialValue === 'object' &&
      initialValue !== null &&
      !isEmptyObject.call(initialValue)
    ) {
      value = objToQueryString(initialValue, key);
      return `${final}${delimiter}${value}`;
    } else {
      value = encodeURIComponent(obj[current]);
      return `${final}${delimiter}${key}=${value}`;
    }
  }, '');
}

function tryTypeCastFromString(item) {
  if (item === '') {
    return '';
  }
  if (isNaN(item)) {
    if (item === 'null') {
      return null;
    }
    if (item === 'undefined') {
      return undefined;
    }
    if (item === 'true') {
      return true;
    }
    if (item === 'false') {
      return false;
    }
    return item;
  }
  return parseFloat(item);
}

export function queryStringToObj(str) {
  let response = {};
  if (str) {
    response = Object.fromEntries(new URLSearchParams(str));
    for (let key in response) {
      if (Object.prototype.hasOwnProperty.call(response, key)) {
        const keyDecoded = decodeURIComponent(key);
        if (key !== keyDecoded) {
          response[keyDecoded] = response[key];
          delete response[key];
        }
      }
    }
    Object.keys(response).forEach(function (key) {
      const matches = key.split(/[[\]]/).filter(Boolean);
      if (matches.length > 1) {
        matches.reduce((ref, nextKey, index) => {
          let keyType = {};
          const isFinalValue = index + 1 === matches.length;
          if (!isFinalValue && /^\d+$/.test(matches[index + 1])) {
            keyType = [];
          }
          ref[nextKey] =
            ref[nextKey] ||
            (isFinalValue ? tryTypeCastFromString(response[key]) : keyType);
          return ref[nextKey];
        }, response);
        delete response[key];
      } else {
        response[key] = tryTypeCastFromString(response[key]);
      }
    });
  }
  return response;
}

export function without(...keys) {
  const obj = {};
  for (let i in this) {
    if (keys.indexOf(i) >= 0) {
      continue;
    }
    if (!Object.prototype.hasOwnProperty.call(this, i)) {
      continue;
    }
    obj[i] = this[i];
  }
  return obj;
}

export function validateForm(formId) {
  let requiredFields = [];
  if (formId) {
    let form = document.getElementById(formId);
    requiredFields = form ? Array.from(form.querySelectorAll('.validate')) : [];
  } else {
    requiredFields = Array.from(document.getElementsByClassName('validate'));
  }
  let validForm = true;
  requiredFields.forEach(function (element) {
    let value =
      element.type === 'number' ? parseFloat(element.value) : element.value;
    if (element.classList.contains('select-single')) {
      if (!element.dataset.value) {
        validForm = false;
        element.classList.add('error');
      } else {
        element.classList.remove('error');
      }
    } else if (element.classList.contains('questionnaire')) {
      let requiredMark = element.getElementsByClassName('required-mark')[0];
      let selectedOption = element.getElementsByClassName('is-active');
      if (selectedOption && selectedOption.length > 0) {
        requiredMark.classList.remove('error');
      } else {
        validForm = false;
        requiredMark.classList.add('error');
      }
    } else {
      switch (element.type) {
        case 'number':
        case 'text':
          if (
            element.getAttribute('name').toLocaleLowerCase() ===
            'budgeted_months'
          ) {
            // eslint-disable-next-line eqeqeq
            if (element.value == 0) {
              value = false;
            }
          }
          if (!value) {
            validForm = false;
            element.classList.add('error');
          } else {
            element.classList.remove('error');
          }
          break;
        case 'textarea':
        case 'select-one':
        case 'email':
        case 'password':
        case 'tel':
          if (element.getAttribute('name').toLowerCase() === 'zip') {
            if (element.value.length !== 5) {
              value = false;
            }
          }
          if (element.getAttribute('name').toLowerCase() === 'phone') {
            if (
              element.value.trim().length < PHONE_NUMBER_MIN ||
              element.value.trim().length > PHONE_NUMBER_MAX
            ) {
              value = false;
            }
          }
          if (!value) {
            validForm = false;
            element.classList.add('error');
          } else {
            element.classList.remove('error');
          }
          break;
      }
    }
  });
  return validForm;
}

export function activateLoader() {
  let loader = document.getElementById('loader-container');
  if (loader) {
    loader.classList.add('active');
  }
}

export function deactivateLoader() {
  let loader = document.getElementById('loader-container');
  if (loader) {
    loader.classList.remove('active');
  }
}

export function numberValidator(element, size, maxValue, required = false) {
  element.addEventListener('keypress', applyRegex);
  element.addEventListener('keyup', applyRegex);
  element.addEventListener('blur', applyRegex);

  function applyRegex(e) {
    this.value.replace(/[^\d].+/, '');
    if (this.value.length >= size) {
      e.preventDefault();
    }
    if (e.which < 48 || e.which > 57) {
      e.preventDefault();
    }
    if (
      (maxValue && this.value > maxValue) ||
      (required && (!this.value || this.value === '0'))
    ) {
      this.classList.add('error');
    } else {
      this.classList.remove('error');
    }
  }
}

export function getCapabilitiesFromFunction(
  capabilitiesArray,
  selectedFunction,
) {
  let obj = {};
  capabilitiesArray.forEach(row => {
    if (row[2].toString() === selectedFunction) {
      obj[row[1]] = row[0];
    }
  });
  return obj;
}

export function getObjectFromArray(arr, reverse = true) {
  let obj = {};
  arr.forEach(row => {
    if (reverse) {
      obj[row[1]] = row[0];
    } else {
      obj[row[0]] = row[1];
    }
  });
  return obj;
}

export function isNumeric(n) {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

export function getCapabilitiesForFunction(
  functionId,
  capabilities,
  selected_capabilities,
  primary = false,
) {
  let arr_primary = [];
  let arr_secondary = [];
  if (functionId in capabilities) {
    selected_capabilities.forEach(capability => {
      let in_array = false;
      let selectedCapabilityId =
        typeof capability === 'object' ? capability.id : capability;
      capabilities[functionId].forEach(row => {
        let capabilityId = row[0];
        if (capabilityId === selectedCapabilityId) {
          in_array = true;
        }
      });
      if (in_array) {
        arr_primary.push(selectedCapabilityId);
      } else {
        arr_secondary.push(selectedCapabilityId);
      }
    });
  }
  if (primary) {
    return arr_primary;
  }
  return arr_secondary;
}

export function isEmptyString(value) {
  return !value || 0 === value.trim().length;
}

export const capitalize = s => {
  if (typeof s !== 'string') {
    return '';
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const upperFirst = s => {
  if (typeof s !== 'string') {
    return s;
  } else {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
};

export const camelToKebabCase = input => {
  return input.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
};

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const currencyFormatterNoCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
});

export function currencyFormatterRounded(value) {
  if (value % 1 === 0) {
    return currencyFormatterNoCents.format(value);
  } else {
    return currencyFormatter.format(value);
  }
}

export const numberWithDelimiterFormatter = new Intl.NumberFormat('en-US');

export const formatDateTime = (timestamp, dateOptions, timeOptions) => {
  const dateTime = new Date(timestamp);
  const date = dateTime.toLocaleDateString('en-us', dateOptions);
  const time = dateTime.toLocaleTimeString('en-us', timeOptions);
  return { date: date, time: time };
};

export const unixTimestampAsUtc = unixTimestamp => {
  if (unixTimestamp) {
    const value = unixTimestamp * 1000;
    const dateObj = new Date(value);
    return dateObj.toISOString();
  }
  return;
};

export const showMessage = (message, error = false) => {
  let noticeContainer = document.querySelector('.notice-wrapper');
  if (noticeContainer) {
    let notice = noticeContainer.firstElementChild;
    notice.textContent = message;
    noticeContainer.classList.add('show-notice');
    if (error) {
      noticeContainer.classList.add('error');
    } else {
      noticeContainer.classList.remove('error');
    }
    setTimeout(() => noticeContainer.classList.remove('show-notice'), 5000);
  }
};

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const getMonthAsString = month => monthNames[month];

export const getIndustryName = (industryId, industries) => {
  const industry = industries.find(element => {
    return element[1] === industryId;
  });
  return industry ? industry[0] : '';
};

export const getHoneyBadgerConfig = () => {
  return HONEYBADGER_CONFIG;
};

export const isPreviousProject = status => {
  switch (status) {
    case PROJECT_STATUS.cancelled:
    case PROJECT_STATUS.filled:
    case PROJECT_STATUS.onboarding:
    case PROJECT_STATUS.placed:
      return true;
    default:
      return false;
  }
};

export const isDisabledBid = status => {
  switch (status) {
    case BID_STATUS.accepted:
    case BID_STATUS.offer_extended:
    case BID_STATUS.denied:
      return true;
    default:
      return false;
  }
};

export const getLastYears = (years = 30) => {
  const currentYear = new Date().getFullYear();
  let yearsArray = [];
  for (let i = 0; i <= years; i++) {
    const value = (currentYear - i).toString();
    yearsArray.push([value, value]);
  }
  return yearsArray;
};

/**
 * Collects primitive values into an array. This is used to grab all string
 * values from an API response which has been recently deserialized.
 * @param {Object} obj An object of objects, arrays, null, numbers, strings
 * @param {Array} [values=[]] An array to which this function will push found primitives
 * @returns {Array} An array of found primitives
 */
export function getPrimitives(obj, values = []) {
  return Object.values(obj).reduce((final, val) => {
    if (typeof val === 'object' && val !== null) {
      return getPrimitives(val, final);
    }
    values.push(val);
    return values;
  }, values);
}

export function isFocusable(element) {
  if (
    element.tabIndex > 0 ||
    (element.tabIndex === 0 && element.getAttribute('tabIndex') !== null)
  ) {
    return true;
  }

  if (element.disabled) {
    return false;
  }

  switch (element.nodeName) {
    case 'A':
      return !!element.href && element.rel !== 'ignore';
    case 'INPUT':
      return element.type !== 'hidden' && element.type !== 'file';
    case 'BUTTON':
    case 'SELECT':
    case 'TEXTAREA':
      return true;
    default:
      return false;
  }
}

export function attemptFocus(element) {
  if (!isFocusable(element)) {
    return false;
  }
  try {
    element.focus();
  } catch (_e) {
    // continue regardless of error
  }
  return document.activeElement === element;
}

export function focusFirstDescendant(element) {
  for (var i = 0; i < element.childNodes.length; i++) {
    var child = element.childNodes[i];
    if (attemptFocus(child) || focusFirstDescendant(child)) {
      return true;
    }
  }
  return false;
}

export function focusLastDescendant(element) {
  for (var i = element.childNodes.length - 1; i >= 0; i--) {
    var child = element.childNodes[i];
    if (attemptFocus(child) || focusLastDescendant(child)) {
      return true;
    }
  }
  return false;
}

export function PreventableEvent(value, attributes = {}) {
  this.target = { value, ...attributes };
  this.defaultPrevented = false;
  this.preventDefault = () => {
    this.defaultPrevented = true;
  };
}

function getUnicodeFirstChar(string) {
  const unicodeChar = string.codePointAt(0);
  const initial = String.fromCodePoint(unicodeChar).toUpperCase();
  return initial;
}

export function getInitialsFullName(fullName) {
  if (fullName.indexOf(' ') === -1) {
    return '';
  } else {
    let firstInitial = getUnicodeFirstChar(fullName.split(' ')[0]);
    let lastInitial = getUnicodeFirstChar(fullName.split(' ')[1]);
    return firstInitial + lastInitial;
  }
}

export function getInitialsFirstAndLastName(firstName, lastName) {
  if (firstName && lastName) {
    let firstInitial = getUnicodeFirstChar(firstName);
    let lastInitial = getUnicodeFirstChar(lastName);
    return firstInitial + lastInitial;
  } else {
    return '';
  }
}

export function getFirstNameAndInitialForLastName(fullName = '') {
  if (fullName.indexOf(' ') === -1) {
    return '';
  }

  const firstName = fullName.split(' ')[0];
  const lastInitial = getUnicodeFirstChar(fullName.split(' ')[1]);

  return `${firstName} ${lastInitial}.`;
}

export function toPhone(phone) {
  return (phone || '')
    .toString()
    .replace(
      /^[+\d{1,3}\-\s]*\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})(.*)$/,
      '($1) $2-$3 $4',
    )
    .trim();
}

export function cleanUrl(url) {
  return url
    .trim()
    .replace(/^https?:\/\//, '') // Remove the https://
    .replace(/^www\./, '') // Remove www.
    .replace(/\?.*$/, '') // Remove a query string
    .replace(/#$/, '') // Remove an anchor
    .replace(/\/$/, ''); // Remove a trailing slash
}

export function isMobileDevice() {
  let isMobile = false;
  //prettier-ignore
  (function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) { isMobile = true; } })(navigator.userAgent || navigator.vendor || window.opera); // eslint-disable-line
  return isMobile;
}

/**
 * Splits an array into groups of a given number
 * @example chunk([a,b,c,d], 2) // return; [[a,b],[c,d]]
 * @param {Array} array An array you would like to split into chunks
 * @param {Number} n The number of array items in each chunk
 * @returns {Array.<Array>} A new array with the original array split into groups of n items.
 */
export function chunk(array, n) {
  if (!array.length) {
    return [];
  }
  return [array.slice(0, n)].concat(chunk(array.slice(n), n));
}

// Given an array with a string label and a value,
// return an <option with that label and value.
export const optionBuilder = ([label, value]) => (
  <option data-testid={value} key={value} value={value}>
    {label}
  </option>
);

// Given a response from ReactiveRecord, the string name of the model, and a string optionKey -
// return HTML <options for a select component.
export const optionBuilderFromModel = (models, modelName, fieldPath) => {
  return models.map(value => (
    <option
      key={value.id}
      value={value.id}
      data-testid={`${modelName}-${value.id}`}
    >
      {value[fieldPath]}
    </option>
  ));
};

// Given a response from ReactiveRecord and an optionKey,
// return an object that can be passed in as options to a multi-select component.
// Example:
//
// const response = [
//   {id: 1, title: 'foo', description: "nonsense"},
//   {id: 2, title: 'bar', description: "other nonsense"}
// ]
//
// toOptions(response, 'title')
// returns: {1: 'foo', 2: 'bar'}
export const toMultiSelectObject = (response, optionKey) => {
  return response.reduce((accum, model) => {
    accum[model.id] = model[optionKey];
    return accum;
  }, {});
};

/**
 * Returns a new array of unique items, based on Set functionality, which does not do a deep comparison of objects.
 * @example uniqueNaive([1, 1, 2, 3]) // return; [1, 2, 3]
 * @param {Array} array An array you would like to make unique
 * @returns {Array} A new array with only unique items from the original array
 */
export const uniqueNaive = array => Array.from(new Set(array));

const nameReducer = (shortened, month) =>
  shortened.replace(new RegExp(month, 'g'), month.slice(0, 3));
/**
 * Returns a string with month names abbreviated to first 3 characters.
 * @example shortenMonthNames('January 1 - May 1') // => 'Jan 1 - May 1'
 * @param {String} string A string which could contain full month names such as January
 * @returns {String} A string with month names abbreviated to first 3 characters
 */
export const shortenMonthNames = string =>
  monthNames.reduce(nameReducer, string);

const commaSeparateThousands = num =>
  num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');

export const prettyFormatNumericRanges = ranges =>
  ranges.map(range => {
    const [min, max] = range.split('..').map(commaSeparateThousands);
    return max.length === 0 ? `${min} or more` : `${min} - ${max}`;
  });

/* Selects capabilities that are within a primary capabilities category if defined */
export const inCapabilitiesCategory =
  primaryCapabilitiesCategoryId => capability => {
    if (primaryCapabilitiesCategoryId) {
      return (
        capability.capabilities_category_id.toString() ===
        primaryCapabilitiesCategoryId.toString()
      );
    }
    return false;
  };

export const handleKeyboardActivation = next => event => {
  if (/^Enter|\s$/.test(event.key)) {
    event.preventDefault();
    next(event);
  }
};

export const toCamelCase = name =>
  name
    .toLowerCase()
    .replace('&', '')
    .split(' ')
    .map((item, index) => (index === 0 ? item : capitalize(item)))
    .join('');

export const getNextTooltipPositioning = (
  ref,
  spacing,
  overrideOrientRight,
  overrideOrientTop,
) => {
  const nextTooltipPositioning = {
    show: true,
    popupStyle: {
      position: 'absolute',
    },
    portalStyle: {
      position: 'absolute',
    },
  };
  /* Place the menu in the most sensible location based on space available on the page */
  const { pageXOffset, pageYOffset, innerWidth, innerHeight } = window;
  const { top, right, bottom, left, width } = ref.getBoundingClientRect();
  const orientRight = overrideOrientRight || left > innerWidth - right;
  const orientTop = overrideOrientTop || top > innerHeight - bottom;
  nextTooltipPositioning.caretPosition = width / 2;
  if (orientRight) {
    nextTooltipPositioning.orientRight = true;
    nextTooltipPositioning.portalStyle.left = `${
      pageXOffset + right + spacing
    }px`;
    nextTooltipPositioning.popupStyle.right = '0';
  } else {
    nextTooltipPositioning.portalStyle.left = `${
      pageXOffset + left - spacing
    }px`;
    nextTooltipPositioning.popupStyle.left = '0';
  }
  if (orientTop) {
    nextTooltipPositioning.orientTop = true;
    nextTooltipPositioning.portalStyle.top = `${
      pageYOffset + top - spacing * 2
    }px`;
    nextTooltipPositioning.popupStyle.bottom = '0';
  } else {
    nextTooltipPositioning.portalStyle.top = `${
      pageYOffset + bottom + spacing * 2
    }px`;
    nextTooltipPositioning.popupStyle.top = '0';
  }
  return nextTooltipPositioning;
};

/**
 * Useful when wanting ReactiveRecord's validation functionality on a native HTML form (on with a real `action` and `method`).
 * This is passed in along with the validation object to a ReactiveRecord input in order for the library to fully use validations.
 * If you'd like to disable inputs while validation is occuring, do not use this. Use a similarly shaped object but with state.
 */
export const nativeFormStub = {
  /* eslint-disable no-empty-function */
  decreaseValidation() {},
  increaseValidation() {},
  /* eslint-enable no-empty-function */
};

/**
 * Returns a date formatted as: Wednesday, January 3
 * @param {Date} date
 * @param {Object} options format in which the format will be displayed. For instance, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }
  will display "Monday, November 22, 2021"
 * For more info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 */
export const getFormattedDate = (
  date,
  options = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  },
) => Intl.DateTimeFormat(navigator.language, options).format(date);

// Selects the browser's timezone
export const getTimeZoneFromBrowser = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export const setQueryStringWithoutPageReload = qsValue => {
  const loc = window.location;
  const newUrl = loc.protocol + '//' + loc.host + loc.pathname + qsValue;
  window.history.pushState({ path: newUrl }, '', newUrl);
};

export const clearQueryStringWithoutPageReload = () => {
  setQueryStringWithoutPageReload('');
};

export const setQueryStringValue = (
  key,
  value,
  queryString = window.location.search,
) => {
  const params = new URLSearchParams(queryString);
  params.set(key, value);
  setQueryStringWithoutPageReload(`?${params.toString()}`);
};

export const getQueryParams = () => {
  return new URLSearchParams(window.location.search);
};

export const getQueryStringValue = (
  key,
  queryString = window.location.search,
) => {
  const params = getQueryParams(queryString);
  return params.get(key);
};

export const toKebabCase = name =>
  name
    .toLowerCase()
    .replace(/[^\w\s]/gi, '')
    .split(' ')
    .map(item => item)
    .join('-');

/**
 * Given an array of keys, a new object will be created removing anything that
 * is not listed as part of the keys.
 * Usage: Given the object { a: 1, b: 2, c: 3 } this function will do the following:
 * only.call(myObject, 'b', 'c')
 * result: { b: 2, c: 3 }
 * @param  {Array} keys: List of the only keys as String that will be allowed
 * @returns A new object with only the keys provided.
 */
export function only(...keys) {
  const obj = {};
  for (let i in this) {
    if (
      keys.indexOf(i) >= 0 ||
      !Object.prototype.hasOwnProperty.call(this, i)
    ) {
      obj[i] = this[i];
    }
  }
  return obj;
}

export function getDateWithoutTime(dateString) {
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);
  return date;
}

// groupBy groups given items by given key - this cannot handle nested keys.
// Example:
// users = [
//   { name: 'Amos', id: 1 },
//   { name: 'Naomi', id: 2 },
//   { name: 'Amos', id: 3 }
// ]
//
// groupBy(users, 'name')
//
// {
//    Amos: [
//      { name: 'Amos', id: 1 },
//      { name: 'Amos', id: 3 }
//    ],
//    Naomi: [{ name: 'Naomi', id: 2 }]
//  }
export const groupBy = (items, key) => {
  return items.reduce((accum, item) => {
    (accum[item[key]] = accum[item[key]] || []).push(item);
    return accum;
  }, {});
};

export const scrollToFirstError = async () => {
  if (!('scrollBehavior' in document.documentElement.style)) {
    await import('scroll-behavior-polyfill');
  }
  const errorList = document.querySelectorAll('[class^="ErrorMessage"]');
  if (errorList.length > 0) {
    errorList[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

// NOT meant for security uses, only for stripping html tags from text from trusted sources
export const stripHTMLTags = string => string.replace(/(<([^>]+)>)/gi, '');

export const isEmpty = value => {
  if (value !== undefined && value !== null) {
    if (
      typeof value === 'boolean' ||
      Array.isArray(value) ||
      typeof value === 'object'
    ) {
      return false;
    }
    if (isNaN(parseInt(value))) {
      return isEmptyString(value);
    } else {
      return false;
    }
  }
  return true;
};
/**
 * This method returns undefined
 */
export function noop() {
  // No operation performed.
}

export const getTimeFromNow = date => {
  const elapsedTimeMs = Math.round((+new Date() - new Date(date)) / 1000);

  const MINUTE = 60;
  const HOUR = MINUTE * 60;
  const DAY = HOUR * 24;
  const MONTH = DAY * 30;
  const YEAR = DAY * 365;

  if (elapsedTimeMs < 30) {
    return 'just now';
  } else if (elapsedTimeMs < MINUTE) {
    return elapsedTimeMs + ' seconds ago';
  } else if (elapsedTimeMs < 2 * MINUTE) {
    return 'a minute ago';
  } else if (elapsedTimeMs < HOUR) {
    return Math.floor(elapsedTimeMs / MINUTE) + ' minutes ago';
  } else if (Math.floor(elapsedTimeMs / HOUR) === 1) {
    return '1 hour ago';
  } else if (elapsedTimeMs < DAY) {
    return Math.floor(elapsedTimeMs / HOUR) + ' hours ago';
  } else if (elapsedTimeMs < DAY * 2) {
    return 'yesterday';
  } else if (elapsedTimeMs < MONTH) {
    return Math.floor(elapsedTimeMs / DAY) + ' days ago';
  } else if (elapsedTimeMs < YEAR) {
    return Math.floor(elapsedTimeMs / MONTH) + ' months ago';
  } else {
    return Math.floor(elapsedTimeMs / YEAR) + ' years ago';
  }
};
